/**
 * Zugriff auf die API. Eine Stelle für Fehlerbehandlung und Sitzungsverlust.
 */

export class ApiFehler extends Error {
  constructor(
    public readonly status: number,
    nachricht: string,
    /**
     * Der vollständige Fehlerkörper des Servers.
     *
     * Manche Fehler tragen mehr als eine Meldung – die Dublettenprüfung nennt
     * die gefundenen Kunden, damit die Maske sie zeigen kann, statt nur „geht
     * nicht" zu sagen.
     */
    public readonly daten: unknown = null,
  ) {
    super(nachricht);
    this.name = 'ApiFehler';
  }
}

async function anfrage<T>(pfad: string, optionen: RequestInit = {}): Promise<T> {
  const antwort = await fetch('/api' + pfad, {
    credentials: 'same-origin',
    headers:
      optionen.body && !(optionen.body instanceof FormData)
        ? { 'Content-Type': 'application/json', ...(optionen.headers ?? {}) }
        : (optionen.headers ?? {}),
    ...optionen,
  });

  if (antwort.status === 204) return undefined as T;

  const text = await antwort.text();
  let daten: unknown = null;
  if (text) {
    try {
      daten = JSON.parse(text);
    } catch {
      daten = null;
    }
  }

  if (!antwort.ok) {
    const meldung =
      (daten as { fehler?: string } | null)?.fehler ??
      (antwort.status === 401
        ? 'Die Sitzung ist abgelaufen. Bitte neu anmelden.'
        : 'Die Anfrage konnte nicht verarbeitet werden.');
    throw new ApiFehler(antwort.status, meldung, daten);
  }

  return daten as T;
}

export const api = {
  hole: <T>(pfad: string) => anfrage<T>(pfad),

  sende: <T>(pfad: string, koerper?: unknown) =>
    anfrage<T>(pfad, { method: 'POST', body: koerper === undefined ? undefined : JSON.stringify(koerper) }),

  aendere: <T>(pfad: string, koerper?: unknown) =>
    anfrage<T>(pfad, { method: 'PATCH', body: koerper === undefined ? undefined : JSON.stringify(koerper) }),

  ersetze: <T>(pfad: string, koerper?: unknown) =>
    anfrage<T>(pfad, { method: 'PUT', body: koerper === undefined ? undefined : JSON.stringify(koerper) }),

  loesche: <T>(pfad: string) => anfrage<T>(pfad, { method: 'DELETE' }),

  datei: <T>(pfad: string, formular: FormData) =>
    anfrage<T>(pfad, { method: 'POST', body: formular }),
};
